import {
  createContext,
  useContext,
  useMemo,
  JSX,
  ReactNode,
  useState,
  useCallback,
} from "react";

type AnalyticsState = {
  quoteProductType: string;
  quoteCarrierName: string;
  quoteRequoteReason: string;
  carrierConnectedNames: string[];
  carrierConnectedCount: number;
  carrierUnconnectedNames: string[];
  carrierUnconnectedCount: number;
  coverageCurrentNames: string[];
  coverageCurrentCount: number;
  coverageNewNames: string[];
  coverageNewCount: number;
  companyName: string;
  brokerName: string;
  rfpId: string;
};

const initialState: AnalyticsState = {
  quoteProductType: "",
  quoteCarrierName: "",
  quoteRequoteReason: "",
  carrierConnectedNames: [],
  carrierConnectedCount: 0,
  carrierUnconnectedNames: [],
  carrierUnconnectedCount: 0,
  coverageCurrentNames: [],
  coverageCurrentCount: 0,
  coverageNewNames: [],
  coverageNewCount: 0,
  companyName: "",
  brokerName: "",
  rfpId: "",
};

type AnalyticsContextType = {
  state: AnalyticsState;
  dispatch: (newState: Partial<AnalyticsState>) => void;
};

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

export const AnalyticsProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [state, setState] = useState(initialState);

  const dispatch = useCallback(
    (payload: Partial<AnalyticsState>) => {
      setState((prevState) => ({ ...prevState, ...payload }));
    },
    [setState],
  );

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);
  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsData = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error(
      "useAnalyticsData must be used within an AnalyticsProvider",
    );
  }
  return context;
};
